import { useI18n } from '@fox/extensions/src/use-i18n';
import { useCallback, useEffect, useState } from 'react';
import { useDebounce } from '../../hooks';
import { strIsNullOrEmpty } from '../../utils';

export async function getGoogleAddressSuggestionsAsync<T>(
	address: string,
	region: string,
	convertSuggestion: (r: google.maps.places.AutocompletePrediction) => T,
	types?: string[]
): Promise<T[]> {
	const geocoder = new google.maps.places.AutocompleteService();
	return new Promise<T[]>((resolve, reject) => {
		try {
			geocoder.getPlacePredictions(
				{
					componentRestrictions: {
						country: region
					},
					input: address,
					...(types && types.length > 0 ? { types } : {})
				},
				(res, status) => {
					if (status === google.maps.places.PlacesServiceStatus.OK) {
						resolve(res?.map(r => convertSuggestion(r)) ?? []);
					} else {
						resolve([]);
					}
				}
			);
		} catch (e) {
			reject(e);
		}
	});
}

export async function getGooglePlaceSuggestionsAsync<T>(
	address: string,
	region: string,
	convertSuggestion: (r: google.maps.places.Place) => T | undefined
): Promise<T[]> {
	// TOTAL BS: eslint is smoking crack here, put your mouse over any of the types you see here and it will
	// show you the correct types instead of any or unknown!!! I don't know what you are complaining about!

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
	const { Place } = (await google.maps.importLibrary('places')) as google.maps.PlacesLibrary;
	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
	const { places } = await Place.searchByText({
		fields: ['displayName', 'formattedAddress', 'location', 'viewport', 'addressComponents', 'types'],
		textQuery: address,
		region: region,
		maxResultCount: 10
	});
	// eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
	return places.map(r => convertSuggestion(r)).filter(r => r !== undefined);
}

/**
 * Custom hook that performs a debounced search for Google address suggestions.
 *
 * @template T - The type to which each suggestion will be converted.
 * @param valueToSearch - The input string to search for address suggestions.
 * @param delay - The debounce delay in milliseconds before triggering the search.
 * @param convertSuggestion - A function that converts a Google AutocompletePrediction to the desired type T.
 * @returns The result of the debounced address search, typed as T.
 */
export function useDebouncedGoogleAddressSearch<T>(
	valueToSearch: string,
	delay: number,
	convertSuggestion: (r: google.maps.places.AutocompletePrediction) => T
) {
	return useDebouncedAddressSearch(valueToSearch, delay, (addr, region) =>
		getGoogleAddressSuggestionsAsync(addr, region, convertSuggestion, undefined)
	);
}

/**
 * Custom React hook that performs a debounced address search using a provided fetcher function.
 *
 * @template T The type of the address search result items.
 * @param valueToSearch - The input string to search for addresses.
 * @param delay - The debounce delay in milliseconds before triggering the search.
 * @param fetcher - An asynchronous function that fetches address suggestions based on the input string and region code.
 * @returns A tuple containing:
 *   - The current list of address suggestions (`T[]`),
 *   - A setter function to update the suggestions,
 *   - A boolean indicating whether the fetch operation is in progress.
 *
 * @remarks
 * The hook debounces the search input to avoid excessive API calls and automatically manages the loading state.
 * It also uses the current region code from the i18n context when fetching suggestions.
 */
export function useDebouncedAddressSearch<T>(valueToSearch: string, delay: number, fetcher: (addr: string, region: string) => Promise<T[]>) {
	const i18n = useI18n();
	const [localResults, setLocalResults] = useState<T[]>([]);
	const debouncedAddress = useDebounce(valueToSearch, delay);
	const [isFetching, setIsFetching] = useState<boolean>(false);
	useEffect(() => {
		function querySuggestions() {
			if (!strIsNullOrEmpty(debouncedAddress)) {
				setIsFetching(true);
				fetcher(debouncedAddress, i18n.countryCode)
					.then(list => {
						setLocalResults(list);
						setIsFetching(false);
					})
					.catch(() => setIsFetching(false));
			}
		}
		querySuggestions();
	}, [debouncedAddress]);
	return [localResults, setLocalResults, isFetching] as const;
}

/**
 * Helper function to get place details including address components using the Geocoder API.
 * This is used to retrieve the short_name (abbreviation) for states.
 *
 * @param placeId - The Google Place ID to fetch details for
 * @returns The geocoder result containing address components, or null if not found
 */
async function getPlaceDetails(placeId: string): Promise<google.maps.GeocoderResult | null> {
	const geocoder = new google.maps.Geocoder();
	return new Promise((resolve) => {
		geocoder.geocode({ placeId }, (results, status) => {
			if (status === google.maps.GeocoderStatus.OK && results && results.length > 0) {
				resolve(results[0]);
			} else {
				resolve(null);
			}
		});
	});
}

/**
 * Custom React hook that performs a debounced search for partial address components.
 *
 * @param valueToSearch - The input string to search for (e.g., "vic", "3000", "melbourne")
 * @param delay - The debounce delay in milliseconds before triggering the search
 * @param addressPartType - The type of address component to search for
 * @param countryCode - The ISO country code to restrict results to (e.g., "au", "us")
 * @returns A tuple containing:
 *   - The current list of address part suggestions with `value` and `label` properties
 *   - A setter function to update the suggestions
 *   - A boolean indicating whether the fetch operation is in progress
 *
 * @remarks
 * - For states: Returns abbreviations as values (e.g., "VIC", "NSW", "CA") and full names as labels.
 *   Uses the Geocoder API to fetch place details and extract the short_name from address components.
 *   Results are deduplicated by abbreviation to handle multiple language results.
 * - For localities: Returns the locality name as both value and label with full description
 * - For postcodes: Returns the postcode as both value and label
 *
 * @example
 * // Search for Australian states
 * const [states, setStates, loading] = useDebouncedPartialAddressSearch('vic', 500, 'state', 'au');
 * // Returns: [{ value: 'VIC', label: 'Victoria' }]
 *
 * @example
 * // Search for postcodes
 * const [postcodes, , loading] = useDebouncedPartialAddressSearch('3000', 500, 'postcode', 'au');
 * // Returns: [{ value: '3000', label: '3000, Melbourne VIC, Australia' }]
 */
export function useDebouncedPartialAddressSearch(
	valueToSearch: string,
	delay: number,
	addressPartType: 'postcode' | 'locality' | 'state',
	countryCode: string
) {
	const fetcher = useCallback(
		async (addr: string) => {
			// Map address part types to Google Places API types
			const typeMapping: Record<typeof addressPartType, string[]> = {
				postcode: ['postal_code'],
				locality: ['(cities)'],
				state: ['administrative_area_level_1']
			};

			const types = typeMapping[addressPartType];

			// Get autocomplete predictions from Google Places API
			const predictions = await getGoogleAddressSuggestionsAsync(addr, countryCode, prediction => prediction, types);

			// For states, fetch full place details to get abbreviations (short_name)
			// This ensures values match how addresses are stored in es
			if (addressPartType === 'state') {
				const uniqueStates = new Map<string, { value: string; label: string }>();

				// Fetch details for all predictions in parallel
				await Promise.all(
					predictions.map(async prediction => {
						try {
							const details = await getPlaceDetails(prediction.place_id);
							if (details) {
								// Extract the state/province component
								const stateComponent = details.address_components.find(component =>
									component.types.includes('administrative_area_level_1')
								);

								if (stateComponent?.short_name) {
									// Store abbreviation as value (e.g., "VIC") and full name as label (e.g., "Victoria")
									// Deduplicate by abbreviation in case Google returns multiple language variants
									uniqueStates.set(stateComponent.short_name, {
										value: stateComponent.short_name,
										label: stateComponent.long_name
									});
								}
							}
						} catch {
							// Skip predictions that fail to fetch details
						}
					})
				);

				return Array.from(uniqueStates.values());
			}

			// For postcodes and localities, deduplicate by value to handle multiple language results
			const uniqueResults = new Map<string, { value: string; label: string }>();
			
			predictions.forEach(prediction => {
				const mainText = prediction.structured_formatting?.main_text ?? prediction.description;
				const fullDescription = prediction.description ?? mainText;
				
				// Deduplicate by main text (the actual value) to avoid duplicate entries
				if (!uniqueResults.has(mainText)) {
					uniqueResults.set(mainText, {
						value: mainText,
						label: fullDescription
					});
				}
			});

			return Array.from(uniqueResults.values());
		},
		[addressPartType, countryCode]
	);

	return useDebouncedAddressSearch(valueToSearch, delay, (addr, _region) => fetcher(addr));
}
